<template>
    <access-denied v-if="accessDenied" title="lps" />
    <div id="main" v-else>
        <loader v-if="isLoading" />
        <div class="page">
            <alert-container :messages="msgs" />
            <div class="vertical-flow">
                <div class="listing-header">
                    <div class="title-breadcrumb">
                        <h1 class="title">
                            <span v-if="$route.params.id">
                                {{ $t('editar-lps') }}
                            </span>
                            <span v-else>{{ $t('novo-lps') }}</span>
                            <a class="pl-1" v-if="hide.hideInfo" @click="infoIsHidden()">
                                <b-icon
                                    v-if="!hide.infoIsHidden"
                                    icon="eye-off-outline"
                                    type="is-danger"
                                />
                                <b-icon
                                    v-if="hide.infoIsHidden"
                                    icon="eye-outline"
                                    type="is-success"
                                />
                            </a>
                        </h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a class="a-inativo">{{ $t('promotor') }}</a>
                                </li>
                                <li>
                                    <a class="a-inativo">{{ $t('cadastros') }}</a>
                                </li>
                                <li>
                                    <a @click="$router.push({ name: 'lpss' })">{{ $t('lpss') }}</a>
                                </li>
                                <li>
                                    <a aria-current="page">{{
                                        !lps.idlps ? $t('novo-lps') : lps.nome
                                    }}</a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <div class="header-buttons">
                        <button
                            class="delete-btn"
                            type="button"
                            tag="button"
                            @click="$router.push({ name: 'lps-list' })"
                        >
                            {{ $t('voltar') }}
                        </button>
                        <button 
                            v-if="
                                hasVinculosAtivos &&
                                situacao.id == 0 &&
                                ((!isUpdate && permissao.create) ||
                                    (isUpdate && permissao.update)) &&
                                !lpsIntegracao
                            "
                            class="save-btn" 
                            type="button" 
                            disabled
                            v-tippy 
                            :title="$t('msg-vinculos-ativos-lps')"
                        >
                            {{ $t('salvar') }}
                        </button>
                        <button
                            v-else-if="
                                ((!isUpdate && permissao.create) ||
                                    (isUpdate && permissao.update)) &&
                                !lpsIntegracao
                            "
                            class="save-btn"
                            type="button"
                            @click="saveLPS()"
                            :disabled="isSaving"
                        >
                            {{ $t('salvar') }}
                            <button-loader :isSaving="isSaving" />
                        </button>
                    </div>
                </div>
                <br />
                <div class="form-columns-fit page-container">
                    <div class="columns is-flex-wrap">
                        <div class="column is-3">
                            <v-input
                                ref="idlpserp"
                                v-model="lps.idlpserp"
                                placeholder="placeholder-preencha"
                                :maxlength="30"
                                title="codigo"
                                msg="msg-campo-obrigatorio"
                                :invalid="isNull(lps.idlpserp)"
                                required
                                :disabled="cantEdit"
                            />
                        </div>
                        <div class="column is-3">
                            <vss-select
                                ref="cidadeInput"
                                label="nome"
                                title="cidade-do-promotor"
                                :options="cidades"
                                v-model="cidade"
                                @search="loadCidade"
                                placeholder="cidade-do-promotor-placeholder"
                                :invalid="isNull(cidade) && !lpsIntegracao"
                                msg="msg-campo-obrigatorio"
                                :onBlurFunction="validateIELps"
                                :loading="loadingCidade"
                                required
                                searchable
                            />
                        </div>
                        <div class="column is-3">
                            <vss-select
                                ref="regiaoSelect"
                                title="regiao"
                                :options="regioes"
                                msg="msg-campo-obrigatorio"
                                v-model="regiao"
                                @search="loadRegiao"
                                placeholder="descricao-da-regiao-placeholder"
                                :invalid="isNull(regiao)"
                                required
                            />
                        </div>
                        <div class="column is-3" :class="{ loading: isSearchingCNPJ }">
                            <v-input
                                :hideInfo="hide.hideInfo"
                                :infoIsHidden="hide.infoIsHidden"
                                v-model="lps.cnpj"
                                title="cnpj-do-cliente"
                                placeholder="cnpj-do-cliente-placeholder"
                                :msg="invalidCNPJMsg"
                                :invalid="invalidCNPJ"
                                :onBlurFunction="validateCNPJLps"
                                ref="cnpjInput"
                                v-mask="['##.###.###/####-##']"
                                icon="magnify"
                                @input-action="getInfoByCNPJ"
                                required
                            />
                        </div>
                        <div class="column is-6">
                            <v-input
                                v-model="lps.nome"
                                placeholder="nome-do-cliente-placeholder"
                                :maxlength="100"
                                title="nome-do-cliente"
                                msg="msg-campo-obrigatorio"
                                :invalid="isNull(lps.nome)"
                                ref="nomeInput"
                                required
                            />
                        </div>
                        <div class="column is-6">
                            <v-input
                                v-model="lps.rsocial"
                                :id="'razao-social'"
                                placeholder="razao-social-placeholder"
                                :maxlength="100"
                                title="razao-social"
                                msg="msg-campo-obrigatorio"
                                :invalid="isNull(lps.rsocial)"
                                ref="rsInput"
                                required
                            />
                        </div>
                        <div class="column is-4">
                            <vss-select
                                ref="bandeiraInput"
                                title="bandeira"
                                :options="bandeiras"
                                v-model="bandeira"
                                @search="loadBandeira"
                                :invalid="isNull(bandeira)"
                                placeholder="bandeira-do-promotor-placeholder"
                                msg="msg-campo-obrigatorio"
                                required
                            />
                        </div>
                        <div class="column is-4">
                            <v-input
                                v-model="lps.endereco"
                                placeholder="endereço-do-cliente-placeholder"
                                :maxlength="50"
                                title="endereço-do-cliente"
                                msg="msg-campo-obrigatorio"
                                :invalid="isNull(lps.endereco)"
                                ref="enderecoInput"
                                required
                            />
                        </div>
                        <div class="column is-4">
                            <v-input
                                ref="numeroInput"
                                v-model="lps.numero"
                                :vMask="['##########']"
                                placeholder="numero-cliente-placeholder"
                                title="numero"
                            />
                        </div>
                        <div class="column is-4">
                            <v-input
                                :hideInfo="hide.hideInfo"
                                :infoIsHidden="hide.infoIsHidden"
                                v-model="lps.cep"
                                title="cep-do-cliente"
                                placeholder="cep-do-cliente-placeholder"
                                :msg="invalidCEPMsg"
                                :invalid="invalidCEP"
                                :onBlurFunction="validateCEPLps"
                                ref="cepInput"
                                v-mask="['##.###-###']"
                                required
                            />
                        </div>
                        <div class="column is-4">
                            <v-input
                                v-model="lps.bairro"
                                placeholder="bairro-cliente-placeholder"
                                :maxlength="50"
                                title="bairro-cliente"
                                msg="msg-campo-obrigatorio"
                                :invalid="isNull(lps.bairro)"
                                ref="bairroInput"
                                required
                            />
                        </div>
                        <div class="column is-4">
                            <v-input
                                :hideInfo="hide.hideInfo"
                                :infoIsHidden="hide.infoIsHidden"
                                v-model="lps.ie"
                                title="inscricao-estadual-do-cliente"
                                placeholder="inscricao-estadual-do-cliente-placeholder"
                                :onBlurFunction="validateIELps"
                                :invalid="invalidIE"
                                :msg="invalidIEMsg"
                                ref="ieInput"
                                :maxlength="50"
                            />
                        </div>
                        <div class="column is-4">
                            <v-input-tel
                                ref="telefoneInput"
                                :hideInfo="hide.hideInfo"
                                :infoIsHidden="hide.infoIsHidden"
                                v-model="lps.telefone"
                                title="telefone-do-cliente"
                                :msg="invalidTelefoneMsg"
                                :invalid="invalidTelefone"
                                :onBlurFunction="validateTelefoneLps"
                                :edit="inputTelefoneEdit"
                            />
                        </div>
                        <div class="column is-4">
                            <v-input-tel
                                ref="celularInput"
                                :hideInfo="hide.hideInfo"
                                :infoIsHidden="hide.infoIsHidden"
                                v-model="lps.celular"
                                title="celular-do-cliente"
                                :edit="inputCelularEdit"
                                :msg="invalidCelularMsg"
                                :invalid="invalidCelular"
                                icon="content-copy"
                                :onBlurFunction="validateCelularLps"
                                @input-action="copyWhatsApp"
                            />
                        </div>
                        <div class="column is-4">
                            <v-input-tel
                                ref="faxInput"
                                :hideInfo="hide.hideInfo"
                                :infoIsHidden="hide.infoIsHidden"
                                v-model="lps.fax"
                                title="fax-do-cliente"
                                :edit="inputFaxEdit"
                                :msg="invalidFaxMsg"
                                :invalid="invalidFax"
                                :onBlurFunction="validateFaxLps"
                                v-if="!copyWhatsAppActive"
                            />
                        </div>
                        <div class="column is-4">
                            <v-input
                                :id="generateId(144)"
                                v-model="lps.email"
                                type="email"
                                placeholder="email-do-cliente-placeholder"
                                :maxlength="50"
                                title="email-do-cliente"
                                :msg="invalidEmailMsg"
                                :invalid="invalidEmail"
                                :onBlurFunction="validateEmailLps"
                                ref="emailInput"
                            />
                        </div>
                        <div class="column is-4">
                            <vss-select
                                :id="generateId(145)"
                                title="situacao"
                                ref="situacaoSelect"
                                :options="situacoes"
                                :value="situacao"
                                @input="updateSituacao"
                                mlLabels
                                placeholder="situacao-da-marca-placeholder"
                                :disabled="lps.contratosinrotas != 0 && situacao.id == 1"
                            />
                        </div>
                        <div class="column is-4">
                            <v-input
                                :id="generateId(146)"
                                ref="facebookInput"
                                v-model="lps.facebook"
                                title="Facebook"
                                placeholder="Facebook"
                                icon="facebook-box"
                                type="text"
                                @input-action="openLink(lps.facebook)"
                                disableMLtitle
                                disableMLplaceholder
                            />
                        </div>
                        <div class="column is-4">
                            <v-input
                                :id="generateId(147)"
                                ref="linkedinInput"
                                v-model="lps.linkedin"
                                title="Linkedin"
                                placeholder="Linkedin"
                                icon="linkedin-box"
                                type="text"
                                @input-action="openLink(lps.linkedin)"
                                disableMLtitle
                                disableMLplaceholder
                            />
                        </div>
                        <div class="column is-4">
                            <v-input
                                :id="generateId(148)"
                                ref="instagramInput"
                                v-model="lps.instagram"
                                title="Instagram"
                                placeholder="Instagram"
                                icon="instagram"
                                type="text"
                                @input-action="openLink(lps.instagram)"
                                disableMLtitle
                                disableMLplaceholder
                            />
                        </div>
                        <div class="column is-4">
                            <v-input
                                :id="generateId(149)"
                                ref="siteInput"
                                v-model="lps.site"
                                title="Site"
                                placeholder="Site"
                                icon="open-in-new"
                                type="text"
                                @input-action="openLink(lps.site)"
                                disableMLtitle
                                disableMLplaceholder
                            />
                        </div>
                        <div class="column is-6">
                            <v-input
                                :id="generateId(150)"
                                ref="latitude"
                                v-model="lps.latitude"
                                placeholder="placeholder-preencha"
                                title="latitude"
                                :invalid="invalidLatitude"
                                :msg="invalidLatitudeMsg"
                                @onBlurFunction="validateLatitudeLps('latitude')"
                                tooltipLabel="informacao-cliente-latitude"
                                tooltipSize="is-small"
                                tooltipPosition="is-top"
                                :maxlength="50"
                                validchars="0123456789-."
                                required
                            />
                        </div>
                        <div class="column is-6">
                            <div class="field has-addons">
                                <div class="control is-expanded">
                                    <v-input
                                        :id="generateId(160)"
                                        ref="longitude"
                                        v-model="lps.longitude"
                                        placeholder="placeholder-preencha"
                                        title="longitude"
                                        :msg="invalidLongitudeMsg"
                                        :invalid="invalidLongitude"
                                        @onBlurFunction="validateLongitudeLps('longitude')"
                                        tooltipLabel="informacao-cliente-longitude"
                                        tooltipSize="is-small"
                                        tooltipPosition="is-top"
                                        :maxlength="50"
                                        validchars="0123456789-."
                                        required
                                    />
                                </div>
                                <div
                                    v-if="!cantEdit"
                                    class="control padding-label padding-label-custom"
                                >
                                    <b-button
                                        @click="openLocationEditor"
                                        icon-right="map-marker"
                                        type="is-primary"
                                        v-tippy 
                                        :title="$t('pesquisar-localizacao')"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <MapasGoogleMapsModal
            v-if="permissao.update && startedMaps"
            :center="center"
            :isUpdating="isSaving"
            :locationEditorOpen="locationEditorOpen"
            :update="isUpdate"
            label="selecionar"
            @updateLocation="updateLocation"
            @onCancelLocationEditor="onCancelLocationEditor"
            :startSearchWith="startSearchWith"
        />
    </div>
</template>

<script>
import MapasGoogleMapsModal from '@/components/shared/maps/MapasGoogleMapsModal';
import ProLpsService from '@/services/promotor/ProLpsService.js';
import EstadoService from '@/services/EstadoService.js';
import CidadeService from '@/services/CidadeService.js';
import RegiaoService from '@/services/RegiaoService.js';
import BandeiraService from '@/services/promotor/BandeiraService.js';
import GoogleMaps from '@/components/shared/maps/MapasGoogleMaps';
import LpsValidation from '@/services/validations/LpsValidation';
import Message from '@/models/Message';
import { TheMask } from 'vue-the-mask';
import * as InscricaoEstadual from '@/functions/IEValidation.js';
import HideInfo from '@/components/shared/HideInfo';
import { mapState } from 'vuex';

export default {
    components: {
        MapasGoogleMapsModal,
        GoogleMaps,
        TheMask,
        HideInfo,
    },
    data() {
        return {
            inputFaxEdit: false,
            inputCelularEdit: false,
            inputTelefoneEdit: false,
            center: { lat: 0, lng: 0 },
            startSearchWith: '',
            startedMaps: false,
            cantEdit: false,
            service: new ProLpsService(this.$http, this.$router),
            estadoservice: new EstadoService(this.$http, this.$router),
            cidadeservice: new CidadeService(this.$http, this.$router),
            regiaoservice: new RegiaoService(this.$http, this.$router),
            bandeiraservice: new BandeiraService(this.$http, this.$router),
            validation: new LpsValidation(this.msgs),

            locationEditorOpen: false,
            windowWidth: window.innerWidth,
            lpsIntegracao: this.$store.state.user.tabelasERPWebServer.includes('PRO_LPS'),
            isSaving: false,
            lps: {
                idlpserp: null,
                idregiao: null,
                probandeira: null,
                idbandeira: null,
                prorede: null,
                cidade: null,
                idcidade: null,
                bairro: null,
                situacao: 1,
                latitude: null,
                longitude: null,
                uf: null,
                cep: null,
                ie: null,
                cnpj: null,
                celular: null,
                fax: null,
                telefone: null,
                email: null,
                endereco: null,
                numero: null,
                rsocial: null,
                nome: null,
                facebook: null,
                linkedin: null,
                instagram: null,
                site: null,
            },
            estado: null,
            estados: [],
            cidade: null,
            cidades: [],
            regiao: null,
            regioes: [],
            rede: null,
            redes: [],
            bandeira: null,
            bandeiras: [],
            editable: false,
            title: '',
            msgs: [],
            isLoading: false,
            isSearchingCNPJ: false,
            canSelectBandeira: false,
            canSelectCidade: false,
            canSelectRegiao: false,
            invalidLatitude: false,
            invalidLatitudeMsg: 'msg-campo-obrigatorio',
            invalidLongitude: false,
            invalidLongitudeMsg: 'msg-campo-obrigatorio',
            invalidIE: false,
            invalideIEMsg: 'msg-campo-obrigatorio',
            invalidCNPJ: true,
            invalidCNPJMsg: 'msg-campo-obrigatorio',
            invalidEmail: false,
            invalidEmailMsg: 'msg-campo-obrigatorio',
            invalidCEP: false,
            invalidCEPMsg: 'msg-campo-obrigatorio',
            invalidIEMsg: 'msg-campo-obrigatorio',
            invalidTelefone: false,
            invalidTelefoneMsg: 'msg-campo-obrigatorio',
            invalidFax: false,
            invalidFaxMsg: 'msg-campo-obrigatorio',
            invalidCelular: false,
            invalidCelularMsg: 'msg-campo-obrigatorio',
            mapOptions: {
                mapTypeControl: false,
                scaleControl: true,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false,
                clickableIcons: false,
            },
            situacao: { label: 'ativo', id: 1 },
            situacoes: [
                { label: 'ativo', id: 1 },
                { label: 'inativo', id: 0 },
            ],
            isUpdate: false,
            copyWhatsAppActive: false,
            loadingCidade: false,
            hasVinculosAtivos: false,
            uf: null,
            iscnpjvalidation: false,
            accessDenied: false,
        };
    },
    watch: {
        cidade: function (newValue, oldValue) {
            this.lps.uf = newValue.descricao;
        }
    },
    created() {
        if (this.lps != null && this.lps != {}) this.editable = true;

        this.permissao = this.hasPermissionAccess('promotor', 'cadastros', 'lpss');
        if (this.lpsIntegracao) this.cantEdit = true;

        if (!this.permissao.read) {
            this.accessDenied = true;
            return;
        }

        this.isUpdate = this.$route.params.id != undefined;

        if (this.$route.params.id) {
            this.editable = false;
            this.cantEdit = !this.permissao.update;
            this.carregarDados(this.$route.params.id);
        }
        this.startedMaps = true;
    },
    computed: {
        ...mapState(['hide']),
    },
    methods: {
        generateId(numberId) {
            return this.$route.name + '-geral-' + numberId;
        },
        runValidations(focusedOut = false) {
            if (focusedOut) {
                // Utilizado para ativar a validação dos campos ao carregar os dados de alteração
                Object.entries(this.$refs).forEach(([key, value]) => {
                    value.focusedOut = true;
                });
            }
            this.validateIELps();
            this.validateLatitudeLps();
            this.validateLongitudeLps();
            this.validateCNPJLps();
            this.validateEmailLps();
            this.validateCEPLps();
            this.validateTelefoneLps();
            this.validateFaxLps();
            this.validateCelularLps();
        },
        infoIsHidden() {
            this.$store.dispatch('infoIsHidden', {
                infoIsHidden: !this.hide.infoIsHidden,
            });
        },
        mapsIsHidden() {
            this.$store.dispatch('mapsIsHidden', {
                mapsIsHidden: !this.hide.mapsIsHidden,
            });
        },
        openLocationEditor() {
            if (this.isUpdate) {
                if (!this.onlyNumbers(this.lps.latitude) || !this.onlyNumbers(this.lps.longitude)) {
                    this.center.lat = 0;
                    this.center.lng = 0;
                } else {
                    this.center.lat = +this.lps.latitude;
                    this.center.lng = +this.lps.longitude;
                }
            }
            if (
                !this.isUpdate &&
                this.isNull(this.lps.latitude) &&
                this.isNull(this.lps.longitude) &&
                !this.isNull(this.cidade)
            ) {
                if (!this.isNull(this.lps.endereco)) this.startSearchWith = this.lps.endereco;
                if (!this.isNull(this.lps.numero))
                    this.startSearchWith = this.startSearchWith + ' ' + this.lps.numero;
                if (!this.isNull(this.lps.bairro))
                    this.startSearchWith = this.startSearchWith + ' ' + this.lps.bairro;
                if (!this.isNull(this.cidade.cidade))
                    this.startSearchWith = this.startSearchWith + ' ' + this.cidade.cidade;
                if (!this.isNull(this.cidade.descricao))
                    this.startSearchWith = this.startSearchWith + ' ' + this.cidade.descricao;
                setTimeout(() => {
                    this.$children[this.$children.length - 1].getLocation(this.startSearchWith);
                }, 1000);
            }
            this.locationEditorOpen = true;
        },
        onCancelLocationEditor() {
            this.locationEditorOpen = false;
            this.startSearchWith = '';
        },
        updateLocation(val) {
            this.isLoadingEdit = true;
            this.center.lat = val.center.lat;
            this.center.lng = val.center.lng;
            this.lps.latitude = this.center.lat;
            this.lps.longitude = this.center.lng;
            this.onCancelLocationEditor();
            this.validateLatitudeLps();
            this.validateLongitudeLps();
            setTimeout(() => (this.isLoadingEdit = false), 50);
        },
        validateLatitudeLps() {
            if (this.isNull(this.lps.latitude)) {
                this.invalidLatitude = true;
                this.invalidLatitudeMsg = 'msg-campo-obrigatorio';
            } else if (
                !this.isNull(this.lps.latitude) &&
                !this.validateLatitude(this.lps.latitude)
            ) {
                this.invalidLatitude = true;
                this.invalidLatitudeMsg = 'msg-campo-invalido';
            } else {
                this.invalidLatitude = false;
            }
        },
        validateLongitudeLps() {
            if (this.isNull(this.lps.longitude)) {
                this.invalidLongitude = true;
                this.invalidLongitudeMsg = 'msg-campo-obrigatorio';
            } else if (
                !this.isNull(this.lps.longitude) &&
                !this.validateLongitude(this.lps.longitude)
            ) {
                this.invalidLongitude = true;
                this.invalidLongitudeMsg = 'msg-campo-invalido';
            } else {
                this.invalidLongitude = false;
            }
        },
        validateCNPJLps() {
            if (this.isNull(this.lps.cnpj)) {
                this.invalidCNPJ = true;
                this.invalidCNPJMsg = 'msg-campo-obrigatorio';
            } else if (!this.isNull(this.lps.cnpj) && !this.validateCnpj(this.lps.cnpj)) {
                this.invalidCNPJ = true;
                this.invalidCNPJMsg = 'msg-campo-invalido';
            } else {
                this.invalidCNPJ = false;
            }
        },
        validateEmailLps() {
            if (!this.isNull(this.lps.email) && !this.validateEmail(this.lps.email)) {
                this.invalidEmail = true;
                this.invalidEmailMsg = 'msg-campo-invalido';
            } else {
                this.invalidEmail = false;
            }
        },
        validateCEPLps() {
            if (this.isNull(this.lps.cep)) {
                this.invalidCEP = true;
                this.invalidCEPMsg = 'msg-campo-obrigatorio';
            } else if (!this.isNull(this.lps.cep) && !this.validateCEP(this.lps.cep)) {
                this.invalidCEP = true;
                this.invalidCEPMsg = 'msg-campo-invalido';
            } else {
                this.invalidCEP = false;
            }
        },
        validateIELps() {
            this.invalidCNPJ = false;
            if (!this.isNull(this.lps.ie) && (this.isNull(this.lps.uf) || this.isNull(this.lps.cnpj))) {
                this.msgs.push(new Message('alert', 'msg-problema-preencher-cidade-cliente', true));
                this.invalidIE = true;
                this.invalidIEMsg = 'msg-campo-invalido';
            } else if (
                !this.isNull(this.lps.ie) &&
                !InscricaoEstadual.validateIE(this.lps.ie, this.lps.uf)
            ) {
                this.invalidIE = true;
                this.invalidIEMsg = 'msg-campo-invalido';
            } else {
                this.invalidIE = false;
            }
        },

        validateTelefoneLps() {
            if (!this.isNull(this.lps.telefone) && !this.validatePhone(this.lps.telefone)) {
                this.invalidTelefone = true;
                this.invalidTelefoneMsg = 'msg-campo-invalido';
            } else {
                this.invalidTelefone = false;
            }
        },
        validateFaxLps() {
            if (!this.isNull(this.lps.fax) && !this.validatePhone(this.lps.fax)) {
                this.invalidFax = true;
                this.invalidFaxMsg = 'msg-campo-invalido';
            } else {
                this.invalidFax = false;
            }
        },
        validateCelularLps() {
            if (!this.isNull(this.lps.celular) && !this.validatePhone(this.lps.celular)) {
                this.invalidCelular = true;
                this.invalidCelularMsg = 'msg-campo-invalido';
            } else {
                this.invalidCelular = false;
            }
        },
        updateSituacao(situacao) {
            if (situacao && situacao.id != null) {
                this.lps.situacao = situacao.id;
                this.situacao = situacao;
            }
        },
        getSituacao() {
            return this.situacoes.filter((s) => s.id == this.lps.situacao)[0];
        },
        carregarDados(id) {
            this.isLoading = true;
            this.isLoadingEdit = true;
            this.service
                .find(id)
                .then((body) => {
                    if (body.content) {
                        this.lps = body.content;

                        if (this.lps.lpsinrotas > 0 || this.lps.lpsincontratos > 0) {
                            this.hasVinculosAtivos = true;
                        }
                        this.lps.celular = !body.content.celular ? '' : body.content.celular;
                        this.lps.fax = !body.content.fax ? '' : body.content.fax;
                        this.lps.telefone = !body.content.telefone ? '' : body.content.telefone;
                        this.lps.cnpj = !body.content.cnpj ? '' : body.content.cnpj;
                        this.lps.ie = !body.content.ie ? '' : body.content.ie;
                        this.title = this.lps.nome;
                        this.situacao = this.getSituacao();

                        if (this.lps.cidade.idcidade) {
                            this.loadCidadeById(body.content.cidade.idcidade);
                        }

                        if (body.content.cidade) {
                            this.cidade = {
                                id: body.content.cidade.idcidade,
                                nome: body.content.cidade.descricao,
                            };
                        }

                        if (body.content.probandeira) {
                            this.bandeira = {
                                id: body.content.probandeira.id,
                                nome: body.content.probandeira.descricao,
                                label: body.content.probandeira.descricao,
                            };
                        }
                        if (body.content.probandeira) {
                            this.bandeira.prorede = {
                                ...body.content.prorede,
                                label: body.content.probandeira.descricao,
                            };
                        }
                        if (body.content.regiao) {
                            this.regiao = {
                                id: body.content.regiao.id,
                                label: body.content.regiao.descricao,
                            };
                        }
                        this.loadCidade();
                        this.loadBandeira();
                        this.loadRegiao();

                        this.checkEditTel();
                    } else {
                        this.$router.push({ name: 'lps-new' });
                    }
                })
                .catch((e) => {
                    this.isLoading = false;
                    console.error('Erro ao carregar o LPS! ', e);
                    this.msgs.push(new Message('error', 'msg-erro-carregar-lps', true));
                })
                .finally((_) => {
                    this.runValidations(true);
                    this.isLoading = false;
                    this.startedMaps = true;
                    this.isLoadingEdit = false;
                });
        },
        zeraLatLong() {
            this.lps.latitude = 0;
            this.lps.longitude = 0;
        },

        loadBandeira() {
            this.bandeiraservice
                .findAllCombo()
                .then(this.mapItemsBandeira)
                .then((allbandeiras) => {
                    this.bandeiras = allbandeiras;
                })
                .catch((e) => {
                    console.error('Erro ao carregar a bandeira! ', e);
                    this.msgs.push(new Message('error', 'msg-erro-carregar-bandeira', true));
                });
        },
        mapItemsBandeira(body) {
            let list = body.content.map((i) => {
                return {
                    ...i,
                    label: i.descricao,
                };
            });
            return list;
        },

        loadRegiao(value) {
            this.regiaoservice
                .findAllCombo(value || '')
                .then(this.mapItemsRegiao)
                .then((allregioes) => {
                    this.regioes = allregioes;
                })
                .catch((e) => {
                    console.error('Erro ao carregar a regiao! ', e);
                    this.msgs.push(new Message('error', 'msg-erro-carregar-regiao', true));
                });
        },
        mapItemsRegiao(body) {
            let list = body.content.map((i) => {
                return {
                    id: i.id,
                    label: i.descricao,
                };
            });
            return list;
        },

        saveLPS(redirect = this.redirectToList) {
            if (this.cidade != null) {
                this.lps.idcidade = this.cidade.id;
            } else {
                this.lps.idcidade = null;
            }
            if (this.bandeira != null) {
                this.lps.idbandeira = this.bandeira.id;
                this.lps.prorede = this.bandeira.prorede;
            } else {
                this.lps.idbandeira = null;
            }
            if (this.regiao != null) {
                this.lps.idregiao = this.regiao.id;
            } else {
                this.lps.idregiao = null;
            }
            this.lps = this.mapTosave(
                this.lps,
                'email',
                'datahorainc',
                'facebook',
                'linkedin',
                'instagram',
                'site', 
                'prorede'
            );
            if (this.validation.validate(this.lps, this.$refs)) {
                this.isSaving = true;
                this.service
                    .save(this.lps)
                    .then((body) => {
                        if (body.message == null) {
                            // Mudar para alerta padrão, quando o mesmo for criado
                            let msg = 'msg-lps-salva';
                            if (this.lps.id) msg = 'msg-lps-edita';
                            this.lps = body.content;
                            this.msgs.push(new Message('success', msg, true));
                            redirect(2);
                        } else {
                            if (this.$te(body.message)) {
                                this.msgs.push(new Message('alert', body.message, true));
                            } else {
                                this.msgs.push(
                                    new Message('error', 'msg-problema-realizar-acao', true)
                                );
                            }
                        }
                    })
                    .catch((e) => {
                        if (e.body && e.body.message && this.$te(e.body.message)) {
                            this.msgs.push(
                                new Message('alert', this.$t(e.body.message), true, false)
                            );
                        } else {
                            this.msgs.push(
                                new Message('error', 'msg-problema-realizar-acao', true)
                            );
                        }
                    })
                    .finally(() => {
                        this.isSaving = false;
                        this.isLoading = false;
                    });
            } else {
                this.runValidations();
                this.msgs.push(new Message('alert', 'msg-campos-obrigatorios', true));
                this.isLoading = false;
            }
        },
        redirectToList(time) {
            setTimeout(() => {
                this.$router.push({
                    name: 'lps-list',
                });
            }, time * 1000 + 50);
        },
        camposObrigatoriosGeocode() {
            this.$refs.enderecoInput.focusedOut = true;
            this.$refs.cidadeInput.focusedOut = true;
        },
        getInfoByCNPJ() {
            this.isLoadingEdit = true;

            if (this.isNull(this.cidade)) {
                this.msgs.push(new Message('error', 'msg-erro-pesquisa-cnpj-cidade', true));
                this.invalidCNPJ = true;
                this.invalidCNPJMsg = 'msg-campo-obrigatorio';
            } else if (!this.invalidCNPJ) {
                let uf = this.cidade.descricao;
                let cnpj = this.lps.cnpj;
                this.isSearchingCNPJ = true;
                this.service
                    .getInfoByCNPJ(uf, cnpj)
                    .then((res) => {
                        if (res.status != 200) {
                            if (res.status == 404) {
                                console.error(`CNPJ ${cnpj} não existe.`);
                                this.msgs.push(
                                    new Message('error', 'msg-erro-cnpj-inexistente', true)
                                );
                            } else {
                                console.error(`Retorno: ${res.status}`);
                                this.msgs.push(
                                    new Message('error', 'msg-erro-pesquisa-cnpj', true)
                                );
                            }
                        } else {
                            let r = JSON.parse(res.content);
                            let data = null;
                            if (typeof r == 'object') {
                                r.forEach((e) => {
                                    if (e.situacao == '1') data = e;
                                });
                            } else if (r.length > 0) {
                                data = r[r.length - 1];
                            }
                            if (data != null) {
                                if (!this.isNull(data.fantasia)) {
                                    this.lps.nome = data.fantasia;
                                }
                                this.lps.rsocial = data.nome;
                                this.lps.cep = data.cep;
                                this.lps.endereco = data.logradouro;
                                this.lps.numero = data.numero;
                                this.lps.bairro = data.bairro;
                                if (this.isNull(this.lps.ie)) this.lps.ie = data.ie;
                                this.iscnpjvalidation = true;
                                this.validateCEPLps();
                            } else {
                                console.error(`Não foi encontrado CNPJ ${cnpj} ativo.`);
                                this.msgs.push(
                                    new Message('alert', 'cnpj-nao-existe-inativo', true)
                                );
                            }
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                        this.msgs.push(new Message('error', 'msg-erro-pesquisa-cnpj', true));
                    })
                    .finally((_) => {
                        setTimeout(() => (this.isLoadingEdit = false), 50);
                        this.isSearchingCNPJ = false;
                        if (this.lps.cep) {
                            this.getInfoByCEP();
                        }
                    });
            }
        },
        getInfoByCEP() {
            this.isLoadingEdit = true;
            this.validateCEPLps();
            if (!this.invalidCEP) {
                let cep = this.lps.cep;
                this.isSearchingCEP = true;
                let token = this.$moment().format('!%$MM(yyyy)DD*&');
                token = this.$CryptoJS.MD5(token).toString();

                this.service
                    .getInfoByCEP(cep, token)
                    .then((data) => {
                        this.buscaCep = data.content;
                        this.lps.bairro = data.content.bairro;
                        this.lps.endereco = data.content.logradouro;
                        this.loadCidade(this.removeAcentos(data.content.cidade));
                    })
                    .catch((err) => {
                        console.error(err);
                        this.msgs.push(new Message('error', 'msg-erro-pesquisa-cep', true));
                    })
                    .finally((_) => {
                        this.isSearchingCEP = false;
                        setTimeout(() => (this.isLoadingEdit = false), 50);
                    });
            }
        },
        copyWhatsApp() {
            this.copyWhatsAppActive = true;
            this.lps.fax = this.lps.celular;
            setTimeout(() => {
                this.copyWhatsAppActive = false;
                this.$forceUpdate();
            }, 100);
        },
        openLink(link) {
            if (!link.startsWith('http')) link = `http://${link}`;
            window.open(link, '_blank');
        },
        checkEditTel() {
            this.isLoadingEdit = true;
            this.inputFaxEdit = !this.isNull(this.lps.fax);
            this.inputCelularEdit = !this.isNull(this.lps.celular);
            this.inputTelefoneEdit = !this.isNull(this.lps.telefone);
            setTimeout(() => (this.isLoadingEdit = false), 50);
        },
        loadCidade(cidade, uf = null) {
            this.cidadeservice
                .findAll(0, 100, cidade || '')
                .then((body) => {
                    return body.content.map((i) => {
                        return {
                            id: i.idcidade,
                            idcidade: i.idcidade,
                            cidade: i.cidade,
                            nome: i.descricao,
                            descricao: i.estado.descricao,
                            estado: i.estado,
                        };
                    });
                })
                .then((allcidades) => {
                    this.cidades = Object.assign(allcidades);
                    this.cidades.sort();
                })
                .finally((_) => {
                    // foi colocado aqui para a busca com cep funcionar até com conexão fraca a internet
                    if (!this.isNull(this.buscaCep)) {
                        let idcidade = this.cidades.filter((s) => s.descricao == this.buscaCep.uf);
                        if (idcidade.length > 0) {
                            this.loadCidadeById(idcidade[0].id);
                        } else {
                            if (this.iscnpjvalidation) {
                                this.lps.nome = '';
                                this.lps.rsocial = '';
                                this.lps.cep = null;
                                this.lps.endereco = '';
                                this.lps.numero = null;
                                this.lps.bairro = '';
                                this.lps.ie = null;

                                this.loadCidade();
                                this.iscnpjvalidation = false;
                            }
                            this.msgs.push(new Message('alert', 'cidade-nao-encontrada', true));
                        }
                        this.buscaCep = null;
                    } else if (uf != null) {
                        let idcidade = this.cidades.filter((s) => s.descricao == uf);
                        if (idcidade.length > 0) {
                            this.loadCidadeById(idcidade[0].id);
                        }
                    }
                });
        },
        loadCidadeById(id) {
            this.isLoadingEdit = true;
            this.cidadeservice
                .find(id)
                .then((cidade) => {
                    if (cidade.content != null) {
                        let temp = {
                            id: cidade.content.id,
                            idcidade: cidade.content.id,
                            cidade: cidade.content.cidade,
                            nome: cidade.content.descricao,
                            descricao: cidade.content.estado.descricao,
                            estado: cidade.content.estado,
                        };
                        this.cidade = temp;
                    } else {
                        console.warn('Busca de cidade vazia');
                    }
                }, 350)
                .finally((_) => {
                    this.isLoadingEdit = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: auto;

    .page {
        position: relative;
    }
}
</style>